import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import COLORS from 'constants/colors';
import LAYOUT from 'constants/layout';

import styled from 'styled-components';
import { Col, FlexView, Row, Text } from '@cuemath/comps';
import SelectInput from '../inputs/dropdown';
import { Separator } from '@cuemath/comps';
import TextInput from '../inputs/text';
import DateInput from '../inputs/date';
import TimeInput from '../inputs/time';
import CheckboxInput from '../inputs/checkbox';
import RadioInput from '../inputs/radio';
import { DATE, TIME, INFO, DROPDOWN, RADIO, CHECKBOX, OBJECT } from '../constants';

const CustomRow = styled(Row)`
  & > :first-child {
    padding-left: 0;
  }
  & > :last-child {
    padding-right: 0;
  }

  @media (max-width: 767px) {
    display: block;

    & > * {
      padding-left: 0;
      padding-right: 0;
    }
  }
`;
const MarginWrapper = styled.div`
  margin-right: ${({ showMargin }) => (showMargin ? '12px' : '0px')};
`;

const TEL = 'tel';
const TEXT = 'text';
const EMAIL = 'email';
const NUMBER = 'number';

export default class Form extends PureComponent {
  static isNodeValid(node, values) {
    if (Array.isArray(node)) {
      return node.findIndex(n => !Form.isNodeValid(n, values)) === -1;
    }

    const { id, optional, edges, type } = node;
    const value = values[id];

    let valid = true;

    if (type === DATE) {
      valid = value && DateInput.isValid(value);
    } else if (type === TIME) {
      valid = value && TimeInput.isValid(value);
    } else if (type === INFO) {
      valid = true;
    } else if (!optional) {
      if (typeof value === OBJECT && !Array.isArray(value)) {
        valid = !!value;
      } else {
        valid = value && value.length > 0;
      }
    }

    if (edges && edges[value]) {
      return valid && Form.isValid(edges[value], values);
    }

    return valid;
  }

  static isValid(nodes, values) {
    if (Array.isArray(nodes)) {
      return nodes.findIndex(node => !Form.isNodeValid(node, values)) === -1;
    }

    return Form.isNodeValid(nodes, values);
  }

  static propTypes = {
    nodes: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    values: PropTypes.shape(PropTypes.object.isRequired).isRequired,
    validations: PropTypes.shape(PropTypes.object.isRequired).isRequired,
    error: PropTypes.shape(PropTypes.object.isRequired),
    onChange: PropTypes.func.isRequired,
    disables: PropTypes.shape({}),
    showMargin: PropTypes.bool,
    isSubTree: PropTypes.bool,
  };

  static defaultProps = {
    showMargin: false,
    error: {
      message: '',
      fields: {},
    },
    disables: null,
    isSubTree: false,
  };

  static getErrorMessage({ value, validateFn, msg, optional, ...other }) {
    if (!value && optional) {
      return null;
    }

    if (validateFn) {
      return validateFn(value, other);
    }

    if (!value) {
      return msg || 'Required';
    }

    if (typeof value === 'string' && value.trim() === '') {
      return msg || 'Required';
    }

    return null;
  }

  static getEdgesKeys(edges) {
    return Object.keys(edges).reduce((acc, key) => {
      const edge = edges[key];

      if (Array.isArray(edge)) {
        return [
          ...acc,
          ...edge.reduce(
            (accumulator, e) => [
              ...accumulator,
              ...(e.edges ? [e.id, ...Form.getEdgesKeys(e.edges)] : [e.id]),
            ],
            [],
          ),
        ];
      }

      return [
        ...acc,
        ...(edge.edges ? [edge.id, ...Form.getEdgesKeys(edge.edges)] : [edge.id]),
      ];
    }, []);
  }

  handleOnChange = (node, value, onChange) => {
    const { id, edges, rule, ...other } = node;

    const data = {};
    const validation = {};

    data[id] = value;

    validation[id] = rule
      ? Form.getErrorMessage({
          value,
          ...rule,
          ...other,
        })
      : null;

    if (edges) {
      const edgesKeys = Form.getEdgesKeys(edges);

      edgesKeys.forEach(key => {
        data[key] = undefined;
        validation[key] = undefined;
      });
    }

    onChange(data, validation);
  };

  renderTextInput = (node, value, disabled, onChange, fieldError) => {
    const {
      id,
      type,
      label,
      optional,
      placeholder,
      maxLength,
      autoFocus = false,
      feedbackType,
    } = node || {};

    const { showMargin = false } = this.props;

    const val = v => {
      return type === TEL ? v.target.value : v;
    };

    return (
      <MarginWrapper showMargin={showMargin}>
        <TextInput
          name={id}
          type={type}
          value={value}
          label={label}
          placeholder={placeholder}
          required={optional}
          maxLength={maxLength}
          autoFocus={autoFocus}
          error={fieldError}
          disabled={disabled}
          onChange={v => {
            this.handleOnChange(node, val(v), onChange);
          }}
          feedbackType={feedbackType}
        />
      </MarginWrapper>
    );
  };

  renderDateInput = (node, value, onChange, disabled, fieldError) => {
    const {
      id,
      label,
      type,
      optional,
      placeholder,
      maxLength,
      autoFocus = false,
      max,
      min,
    } = node;

    const { showMargin = false } = this.props;

    return (
      <MarginWrapper showMargin={showMargin}>
        <TextInput
          name={id}
          type={type}
          value={value}
          label={label}
          placeholder={placeholder}
          required={optional || true}
          maxLength={maxLength}
          autoFocus={autoFocus}
          error={fieldError}
          disabled={disabled}
          maxValue={max}
          minValue={min}
          onChange={v => {
            this.handleOnChange(node, v, onChange);
          }}
        />
      </MarginWrapper>
    );
  };

  renderTimeInput = (node, value, onChange, disabled, fieldError) => {
    const { id, label, type, optional, placeholder, maxLength, autoFocus = false } = node;

    const { showMargin = false } = this.props;

    return (
      <MarginWrapper showMargin={showMargin}>
        <TextInput
          name={id}
          type={type}
          value={value}
          label={label}
          placeholder={placeholder}
          required={optional || true}
          maxLength={maxLength}
          autoFocus={autoFocus}
          error={fieldError}
          disabled={disabled}
          onChange={v => {
            this.handleOnChange(node, v, onChange);
          }}
        />
      </MarginWrapper>
    );
  };

  renderSelectInput = (node, value, disables, onChange) => {
    const { id, label, options, isMulti } = node;
    const { showMargin = false } = this.props;
    const disabled = disables && disables[id];

    return (
      <MarginWrapper showMargin={showMargin}>
        <SelectInput
          name={id}
          label={label}
          options={options}
          value={value}
          disabled={disabled}
          isMulti={isMulti}
          onChange={v => {
            this.handleOnChange(node, v, onChange);
          }}
        />
      </MarginWrapper>
    );
  };

  renderInfo = node => {
    const { id, message, color } = node;

    return (
      <Text name={id} color={color} size="medium">
        {message}
      </Text>
    );
  };

  renderRadioInput = (node, value, onChange) => {
    const { feedbackType, id, noSeparatorForRadioBtn, label, options, transpose } =
      node || {};

    return (
      <RadioInput
        name={id}
        label={label}
        options={options}
        selected={value}
        transpose={transpose}
        onChange={v => {
          this.handleOnChange(node, v, onChange);
        }}
        feedbackType={feedbackType}
        noSeparatorForRadioBtn={noSeparatorForRadioBtn}
      />
    );
  };

  renderCheckboxInput = (node, value, onChange) => {
    const { id, label, options, feedbackType } = node;

    return (
      <CheckboxInput
        name={id}
        label={label}
        options={options}
        selected={value || []}
        onChange={v => {
          this.handleOnChange(node, v, onChange);
        }}
        feedbackType={feedbackType}
      />
    );
  };

  renderNode(node, values, validations, disables, onChange) {
    const { id, type, edges } = node;
    const value = values[id];
    const disabled = disables && disables[id];

    const { error } = this.props;
    const fieldError =
      validations[id] || (error && error.fields ? error.fields[id] : null);

    if ([TEXT, EMAIL, TEL, NUMBER].includes(type)) {
      return (
        <Fragment>
          {this.renderTextInput(node, value, disabled, onChange, fieldError)}
          {fieldError && (
            <Text color={COLORS.RED} size="small">
              {fieldError}
            </Text>
          )}
        </Fragment>
      );
    }

    if (type === DATE) {
      return (
        <Fragment>
          {this.renderDateInput(node, value, onChange)}
          {fieldError && (
            <Text color={COLORS.RED} size="small">
              {fieldError}
            </Text>
          )}
        </Fragment>
      );
    }

    if (type === TIME) {
      return (
        <Fragment>
          {this.renderTimeInput(node, value, onChange)}
          {fieldError && (
            <Text color={COLORS.RED} size="small">
              {fieldError}
            </Text>
          )}
        </Fragment>
      );
    }
    if (type === DROPDOWN) {
      const subtree = value && edges && edges[value];

      return (
        <Fragment>
          {this.renderSelectInput(node, value, disables, onChange)}
          {fieldError && (
            <Text color={COLORS.RED} size="small">
              {fieldError}
            </Text>
          )}
          {subtree && (
            <Fragment>
              <Separator height={LAYOUT.GAP} transparent />
              <Form
                validations={validations}
                error={error}
                nodes={edges[value]}
                values={values}
                onChange={onChange}
                isSubTree
              />
            </Fragment>
          )}
        </Fragment>
      );
    }
    if (type === RADIO) {
      const subtree = value && edges && edges[value];

      return (
        <Fragment>
          {this.renderRadioInput(node, value, onChange)}
          {fieldError && (
            <Text color={COLORS.RED} size="small">
              {fieldError}
            </Text>
          )}
          {subtree && (
            <Fragment>
              <Separator height={LAYOUT.GAP} transparent />
              <Form
                validations={validations}
                error={error}
                nodes={edges[value]}
                values={values}
                onChange={onChange}
                isSubTree
              />
            </Fragment>
          )}
        </Fragment>
      );
    }
    if (type === CHECKBOX) {
      return (
        <Fragment>
          {this.renderCheckboxInput(node, value, onChange)}
          {fieldError && (
            <Text color={COLORS.RED} size="small">
              {fieldError}
            </Text>
          )}
        </Fragment>
      );
    }

    return null;
  }

  renderNodes(nodes, values, validations, disables, onChange) {
    return nodes.map((node, index) => {
      if (Array.isArray(node)) {
        return (
          <CustomRow key={index}>
            {node.map(n => (
              <Col key={n.id} xs={12} sm={12 / node.length} gap={8}>
                {this.renderNode(n, values, validations, disables, onChange)}
              </Col>
            ))}
          </CustomRow>
        );
      }

      return (
        <FlexView gap={8} key={node.id}>
          {this.renderNode(node, values, validations, disables, onChange)}
        </FlexView>
      );
    });
  }

  render() {
    const {
      disables,
      error,
      isSubTree,
      nodes,
      onChange,
      validations,
      values,
    } = this.props;

    return (
      <Fragment>
        {!isSubTree && error && error.message && (
          <Fragment>
            <Text color={COLORS.RED} size="small">
              {error.message}
            </Text>
            <Separator height={LAYOUT.GAP / 2} transparent />
          </Fragment>
        )}
        {Array.isArray(nodes)
          ? this.renderNodes(nodes, values, validations, disables, onChange)
          : this.renderNode(nodes, values, validations, disables, onChange)}
      </Fragment>
    );
  }
}
